import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Lock, Loader2, AlertCircle, ChevronDown,
  Wifi, Shield, Droplets, Building2, Sparkles, Users,
  MapPin, Train, Car, Coffee, MessageSquare, Mail, ExternalLink,
  Zap, ThermometerSun, Wind, Clock, Receipt, Check
} from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_LINK = "https://wa.me/442039207045?text=Hi%2C%20I'd%20like%20to%20discuss%20a%20studio%20at%20Clapham.";

// Inspiration images — clearly labelled as concepts
const INSPIRATION = [
  { src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_occupier-portal/artifacts/jlnxzq0r_ChatGPT%20Image%20Sep%2014%2C%202026%2C%2011_59_27%20PM.png', label: 'Illustrative studio concept', alt: 'Concept: treatment room with massage bed, shelving and natural tones' },
  { src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_occupier-portal/artifacts/jj0tt75e_ChatGPT%20Image%20Sep%2014%2C%202026%2C%2011_58_41%20PM.png', label: 'Illustrative studio concept', alt: 'Concept: hair studio with styling chair, backwash and warm lighting' },
];

const NEIGHBOURHOOD = [
  { name: 'Gail\'s Bakery', desc: 'Pastries, coffee and light lunches.', mapUrl: 'https://maps.google.com/?q=Gails+Bakery+71+Abbeville+Road+London+SW4' },
  { name: 'Bistro Union', desc: 'British neighbourhood restaurant.', mapUrl: 'https://maps.google.com/?q=Bistro+Union+40+Abbeville+Road+London+SW4' },
  { name: 'The Abbeville', desc: 'Gastropub with seasonal food.', mapUrl: 'https://maps.google.com/?q=The+Abbeville+67+Abbeville+Road+London+SW4' },
  { name: 'Nue Ground', desc: 'Café with breakfast, lunch and coffee.', mapUrl: 'https://maps.google.com/?q=Nue+Ground+32+Abbeville+Road+London+SW4' },
];

const FAQS = [
  { q: 'How do building access and client arrivals work?', a: 'You have access to the building 24 hours a day, seven days a week, and can set your own opening hours, subject to your licence agreement. We will confirm the practical entry and client-arrival arrangements before you move in.' },
  { q: 'Are utilities included?', a: 'Yes. Water, hot water, electricity and heating are included in your monthly licence fee. Electricity is subject to fair use. Ventilation and air conditioning are included where installed.' },
  { q: 'Is VAT or a service charge added?', a: 'No VAT is added to the licence fee, and there is no separate service charge.' },
  { q: 'Is clinical waste collection included?', a: 'No. Included collection covers general, non-clinical waste only. Chemical, clinical and hazardous waste require your own suitable containers and collection service.' },
  { q: 'Can I bring my own furniture and equipment?', a: 'Yes, subject to suitability. Check dimensions, access, service requirements and working clearances before arranging delivery.' },
  { q: 'Can I personalise my studio?', a: 'We welcome ideas for making the space reflect your brand. Please discuss decorating, fittings and installations with us before carrying out any work.' },
  { q: 'Can another practitioner work with me?', a: 'Please discuss your proposed arrangement with us. Suitability and permission need to be confirmed for your chosen studio and reflected in the agreement.' },
  { q: 'Could I take two studios?', a: 'This may be possible where suitable studios are available. Speak to us about the space you need and how you would like to use it.' },
  { q: 'What costs should I allow for?', a: 'We will provide a clear breakdown of the monthly fee, included services, any additional charges and upfront payments for your chosen studio.' },
  { q: 'Can I visit again?', a: 'Of course. You are welcome to arrange another visit to check the space, take measurements and discuss your ideas.' },
];

// ─── PASSWORD ENTRY ───
function GuideEntry({ onSuccess }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/studio-guide/auth`, { code: code.trim() }, { withCredentials: true });
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="guide-entry">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-8">
            <img src="/logo.webp" alt="Go Freestyle Studios" className="h-12 mx-auto" style={{filter:'contrast(1.15) brightness(0.95)'}} />
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-3">Your next chapter starts here.</h1>
          <p className="text-sm text-[#5C5C55] leading-relaxed">Enter your access code to explore the Clapham studio guide.</p>
        </div>
        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4] mb-4" data-testid="guide-error">
            <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-[#B33A3A]">{error}</p>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <label className="block text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55] mb-1.5">Access code</label>
          <input type="password" value={code} onChange={(e) => { setCode(e.target.value); setError(''); }}
            className="w-full bg-white border border-[#E5E2DC] rounded-xl h-12 px-4 text-sm text-[#1A1A1A] placeholder:text-[#8A8B85] focus:outline-none focus:ring-2 focus:ring-[#3A4035]/20 focus:border-[#3A4035] transition-colors"
            placeholder="Enter your code" autoFocus data-testid="guide-code-input" />
          <button type="submit" disabled={loading || !code.trim()}
            className="w-full mt-4 bg-[#3A4035] text-white rounded-full h-12 text-sm font-medium hover:bg-[#2B3027] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="guide-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4" strokeWidth={1.5} /> Explore the guide</>}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── SECTION NAV ───
const sections = [
  { id: 'studio', label: 'Your studio' },
  { id: 'features', label: 'Features and services' },
  { id: 'personalise', label: 'Personalisation' },
  { id: 'neighbourhood', label: 'Your neighbourhood' },
  { id: 'questions', label: 'Useful questions' },
];

function SectionNav() {
  return (
    <nav className="bg-white/90 backdrop-blur-md border-b border-[#E5E2DC] sticky top-16 z-40" data-testid="guide-section-nav">
      <div className="max-w-4xl mx-auto px-4 sm:px-8 overflow-x-auto">
        <div className="flex items-center gap-1 py-2 min-w-max">
          {sections.map(s => (
            <a key={s.id} href={`#${s.id}`}
              className="px-3 py-1.5 rounded-full text-xs font-medium text-[#5C5C55] hover:bg-[#F2F0EB] hover:text-[#3A4035] transition-colors whitespace-nowrap">
              {s.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}

// ─── GUIDE CONTENT ───
function GuideContent() {
  const scrollToNext = () => {
    const el = document.getElementById('studio');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="guide-content">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Clapham Studio Guide | Go Freestyle Studios</title>
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-[#E5E2DC] z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.webp" alt="Go Freestyle Studios" className="h-12 sm:h-14" style={{filter:'contrast(1.15) brightness(0.95)'}} />
          </Link>
          <span className="text-xs text-[#8A8B85] font-medium tracking-wide">Clapham Studio Guide</span>
        </div>
      </header>

      <SectionNav />

      {/* ── WELCOME ── */}
      <section className="pt-6 pb-10 sm:pb-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8B85] mb-3">Your Clapham studio guide</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A1A1A] tracking-tight leading-tight mb-4">
            Your next chapter at Go Freestyle.
          </h1>
          <p className="text-base text-[#5C5C55] leading-relaxed max-w-xl mx-auto mb-2">
            Explore the possibilities for your business at our Clapham studios. From practical details to ideas for making the space your own, this guide will help you picture your next step.
          </p>
          <p className="text-sm text-[#3A4035] font-medium mb-6">Your experience. Your brand. Space for what comes next.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#3A4035] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#2B3027] transition-colors"
              data-testid="guide-wa-top">
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} /> Discuss your studio
            </a>
            <button onClick={scrollToNext}
              className="inline-flex items-center gap-2 border border-[#E5E2DC] text-[#3A4035] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#F2F0EB] transition-colors">
              Explore the guide <ChevronDown className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </section>

      {/* ── SPACE FOR YOUR BUSINESS ── */}
      <section id="studio" className="py-10 sm:py-14 bg-white scroll-mt-[7rem]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-3 text-center">
            A space for the business you want to build.
          </h2>
          <p className="text-base text-[#5C5C55] leading-relaxed max-w-xl mx-auto text-center mb-8">
            Whether you are moving from a salon room, working from home or planning the next stage of an established practice, your studio should support the way you want to work.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { title: 'Make it your own', desc: 'Create a professional setting that reflects your brand and helps your clients feel welcome.', icon: Sparkles },
              { title: 'Plan around your services', desc: 'Consider the space you need for treatments, consultations, equipment and storage.', icon: Building2 },
              { title: 'Be part of a practitioner community', desc: 'Build your business alongside other health, wellbeing and beauty practitioners, while keeping your own identity.', icon: Users },
            ].map(card => (
              <div key={card.title} className="bg-[#FAF9F6] rounded-2xl p-5">
                <div className="w-9 h-9 rounded-xl bg-[#E8EDE5] flex items-center justify-center mb-3">
                  <card.icon className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-sm font-medium text-[#1A1A1A] mb-1">{card.title}</h3>
                <p className="text-sm text-[#5C5C55] leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-sm text-[#5C5C55]">
            <span className="font-medium text-[#3A4035]">Our mission:</span> To empower practitioners to do more.{' '}
            <Link to="/our-purpose" className="text-[#3A4035] underline underline-offset-2 hover:text-[#2B3027] transition-colors">Learn more</Link>
          </p>
        </div>
      </section>

      {/* ── FEATURES AND SERVICES ── */}
      <section id="features" className="py-10 sm:py-14 scroll-mt-[7rem]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-3 text-center">
            A clear picture of what's included.
          </h2>
          <p className="text-base text-[#5C5C55] leading-relaxed max-w-xl mx-auto text-center mb-8">
            Your monthly licence fee covers your studio and the services below, with no separate service charge and no VAT added to the licence fee.
          </p>

          {/* Included features */}
          <div className="bg-white rounded-2xl border border-[#E5E2DC] p-6 sm:p-8 mb-4">
            <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#3A4035] mb-5">Included in your licence fee</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {[
                { icon: Droplets, text: 'Water and hot water' },
                { icon: Zap, text: 'Electricity (subject to fair use)' },
                { icon: ThermometerSun, text: 'Heating' },
                { icon: Wind, text: 'Ventilation and air conditioning (where installed)' },
                { icon: Wifi, text: 'Shared business broadband and Wi-Fi' },
                { icon: Clock, text: '24-hour building access, seven days a week' },
                { icon: Shield, text: 'Secure building and studio access' },
                { icon: Building2, text: 'Shared facilities' },
                { icon: Droplets, text: 'Communal area cleaning' },
                { icon: Building2, text: 'General waste collection*', sub: 'Collection from the designated building bin.' },
                { icon: Shield, text: 'Building insurance' },
                { icon: Receipt, text: 'No separate service charge, reconciliation or management fee' },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg bg-[#E8EDE5] flex items-center justify-center shrink-0 mt-0.5">
                    <f.icon className="w-3.5 h-3.5 text-[#3A4035]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm text-[#1A1A1A]">{f.text}</p>
                    {f.sub && <p className="text-xs text-[#8A8B85]">{f.sub}</p>}
                  </div>
                </div>
              ))}
            </div>

            {/* Waste and insurance notes — readable size */}
            <div className="mt-5 pt-4 border-t border-[#E5E2DC] space-y-2.5">
              <p className="text-sm text-[#5C5C55] leading-relaxed">
                <span className="font-medium text-[#1A1A1A]">*General, non-clinical waste only.</span> Chemical, clinical and hazardous waste are not included. Practitioners must arrange their own suitable containers and collection service for these waste types.
              </p>
              <p className="text-xs text-[#8A8B85] leading-relaxed">Communal cleaning covers shared areas — cleaning inside individual studios is the practitioner's responsibility.</p>
              <p className="text-xs text-[#8A8B85] leading-relaxed">Building insurance covers the property. Practitioners should arrange their own business and liability insurance.</p>
            </div>
          </div>

          {/* Separate costs */}
          <div className="bg-[#FAF9F6] rounded-2xl p-6 sm:p-8">
            <p className="text-xs tracking-[0.1em] uppercase font-semibold text-[#8A8B85] mb-4">Separate costs and optional extras</p>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-0.5">Business rates</p>
                <p className="text-sm text-[#5C5C55] leading-relaxed">Business rates are not included. We can help with the relief application process; eligibility depends on your circumstances and is not guaranteed.</p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-0.5">Laundry</p>
                <p className="text-sm text-[#5C5C55] leading-relaxed">Laundry facilities are available on a pay-as-you-wash basis.</p>
              </div>
              <div>
                <p className="text-sm font-medium text-[#1A1A1A] mb-0.5">Parking</p>
                <p className="text-sm text-[#5C5C55] leading-relaxed">On-site parking is currently fully booked, so no spaces are available.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PERSONALISATION ── */}
      <section id="personalise" className="py-10 sm:py-14 bg-white scroll-mt-[7rem]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-3 text-center">
            Imagine your business here.
          </h2>
          <p className="text-base text-[#5C5C55] leading-relaxed max-w-xl mx-auto text-center mb-8">
            From furniture and storage to the way clients move through the space, a thoughtful layout can help your studio work for you. Share your services, equipment and ideas with us, and we can explore the possibilities together.
          </p>

          {/* Inspiration gallery with visible labels */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3">
            {INSPIRATION.map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-44 sm:h-56 object-cover" loading="lazy" />
                <span className="absolute bottom-2 left-2 bg-black/50 text-white text-[10px] font-medium px-2 py-0.5 rounded-full backdrop-blur-sm">
                  {img.label}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#5C5C55] text-center leading-relaxed max-w-lg mx-auto mb-2">
            Illustrations are for inspiration only and are not to scale. Studios are supplied unfurnished. Check the agreed specification and take accurate on-site measurements before ordering furniture or equipment.
          </p>
          <p className="text-xs text-[#8A8B85] text-center">Alterations and installations must be agreed in advance.</p>
        </div>
      </section>

      {/* ── NEIGHBOURHOOD ── */}
      <section id="neighbourhood" className="py-10 sm:py-14 scroll-mt-[7rem]">
        <div className="max-w-4xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-2 text-center">
            Your business. Your neighbourhood.
          </h2>
          <p className="text-sm text-[#5C5C55] leading-relaxed max-w-xl mx-auto text-center mb-6">
            Get to know the area around our Clapham studios — from arriving for your first appointment to finding a coffee between clients.
          </p>

          <div className="space-y-4">
            {/* Find us */}
            <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                <h3 className="font-heading text-sm font-medium text-[#1A1A1A]">Find us</h3>
              </div>
              <p className="text-sm text-[#1A1A1A] font-medium mb-1">Abbeville Mews, 88 Clapham Park Road, London SW4 7BX</p>
              <p className="text-sm text-[#5C5C55] leading-relaxed mb-2">The entrance is on Clapham Park Road. Look for the Abbeville Mews entrance and follow signs to Go Freestyle Studios.</p>
              <a href="https://maps.google.com/?q=Abbeville+Mews+88+Clapham+Park+Road+London+SW4+7BX" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#3A4035] font-medium hover:text-[#2B3027] transition-colors">
                Get directions <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
              </a>
            </div>

            {/* Getting here + Parking side by side on desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Train className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                  <h3 className="font-heading text-sm font-medium text-[#1A1A1A]">Getting here</h3>
                </div>
                <p className="text-sm text-[#5C5C55] mb-1"><span className="font-medium text-[#1A1A1A]">Underground:</span> Clapham Common (Northern line)</p>
                <p className="text-sm text-[#5C5C55]"><span className="font-medium text-[#1A1A1A]">Bus:</span> Routes 35, 37, 88, 137, 322, 345</p>
              </div>
              <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Car className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                  <h3 className="font-heading text-sm font-medium text-[#1A1A1A]">Parking</h3>
                </div>
                <p className="text-sm text-[#5C5C55] leading-relaxed mb-2">On-site parking is currently fully booked, so no spaces are available. If travelling by car, please check nearby public parking options and local restrictions before your visit.</p>
                <p className="text-sm text-[#5C5C55] leading-relaxed mb-2">Street parking is controlled by Lambeth Council; always check signs on arrival.</p>
                <a href="https://www.lambeth.gov.uk/parking" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#3A4035] font-medium hover:text-[#2B3027] transition-colors">
                  Lambeth parking info <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              </div>
            </div>

            {/* Coffee and lunch */}
            <div className="bg-white rounded-2xl border border-[#E5E2DC] p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <Coffee className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} />
                <h3 className="font-heading text-sm font-medium text-[#1A1A1A]">Coffee and lunch nearby</h3>
              </div>
              <p className="text-xs text-[#5C5C55] mb-3">Abbeville Road — a short walk from the studios.</p>
              <div className="grid grid-cols-2 gap-2">
                {NEIGHBOURHOOD.map(place => (
                  <div key={place.name} className="bg-[#FAF9F6] rounded-lg px-3 py-2.5">
                    <p className="text-xs font-medium text-[#1A1A1A]">{place.name}</p>
                    <p className="text-[11px] text-[#8A8B85] leading-relaxed mb-1.5">{place.desc}</p>
                    <a href={place.mapUrl} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] text-[#3A4035] font-medium hover:text-[#2B3027] transition-colors inline-flex items-center gap-1">
                      View on map <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── USEFUL QUESTIONS ── */}
      <section id="questions" className="py-10 sm:py-14 bg-white scroll-mt-[7rem]">
        <div className="max-w-2xl mx-auto px-4 sm:px-8">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-6 text-center">
            Useful questions
          </h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`gfaq-${i}`} className="border-0" data-testid={`guide-faq-${i}`}>
                <AccordionTrigger className="bg-[#FAF9F6] rounded-xl px-5 py-4 text-sm font-medium text-[#1A1A1A] hover:bg-[#F2F0EB] hover:no-underline transition-colors text-left [&[data-state=open]]:rounded-b-none [&[data-state=open]]:bg-[#F2F0EB]">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="bg-[#F2F0EB] rounded-b-xl px-5 pb-4 pt-0">
                  <p className="text-sm text-[#5C5C55] leading-relaxed mt-2">{faq.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-xs text-[#8A8B85] text-center mt-6 leading-relaxed max-w-md mx-auto">
            This guide summarises the Clapham studio arrangements. Please refer to your Studio Licence Agreement for the full terms. If there is any difference, the agreement takes precedence.
          </p>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight mb-3">
            Let's explore your next chapter.
          </h2>
          <p className="text-base text-[#5C5C55] leading-relaxed max-w-lg mx-auto mb-6">
            Tell us which studio interests you, what you would like to accommodate and when you hope to move. We'll help you explore the practical details.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-4">
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#3A4035] text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#2B3027] transition-colors"
              data-testid="guide-wa-bottom">
              <MessageSquare className="w-4 h-4" strokeWidth={1.5} /> Discuss your studio
            </a>
            <a href="mailto:hello@gofreestyle.co.uk"
              className="inline-flex items-center gap-2 border border-[#E5E2DC] text-[#3A4035] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-[#F2F0EB] transition-colors">
              <Mail className="w-4 h-4" strokeWidth={1.5} /> Email us
            </a>
          </div>
          <p className="text-sm text-[#8A8B85] mt-4">One neighbourhood. One business. One studio at a time.</p>
        </div>
      </section>
    </div>
  );
}

// ─── MAIN PAGE COMPONENT ───
export default function ClaphamGuidePage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    axios.get(`${API}/studio-guide/verify`, { withCredentials: true })
      .then(r => { if (r.data.authenticated) setAuthenticated(true); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" />
      </div>
    );
  }

  if (!authenticated) return <GuideEntry onSuccess={() => setAuthenticated(true)} />;
  return <GuideContent />;
}
